import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import type { Cliente, Servicio, Empresa, CotizacionItemsEstandar, Seccion, EstadoCotizacion, Moneda, Cotizacion, CentralCosto, CuotaPago, Contacto, ContactoEmpresa, Marca, Markup, ModoPrecio, CotizacionBranding } from '@/lib/types'
import { contactoFacturacionDe } from '@/lib/types'
import { formatCurrency, nextCotNumber, r2, parseMermaidBlocks } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { IconFileText, IconCheckCircle, IconAlertCircle, IconExternalLink } from '@/components/icons/Icons'
import CostCenterBreakdown from '@/components/cotizaciones/CostCenterBreakdown'
import CostosEstimados, { type CostoEstimadoLocal } from '@/components/cotizaciones/CostosEstimados'
import CronogramaPagos from '@/components/cotizaciones/CronogramaPagos'
import MarkupPartner from '@/components/cotizaciones/MarkupPartner'
import { aplicarMarkup, escalarCronograma, esMarcable } from '@/lib/markup'
import { MODOS_PRECIO, armarBranding, camposDesdeModo, marcaPorNombre, modoDesdeCotizacion, modoLlevaIgv } from '@/lib/modo-precio'
import { useApi } from '@/hooks/useApi'
import { PageHeader, Breadcrumbs } from '@/components/ui'

/** La empresa de la cotización todavía no existe en el directorio. */
const SIN_EMPRESA = 'sin-empresa' as const

/** Marca de una cotización cuyo branding se puso a mano y no está en el catálogo. */
const MARCA_MANUAL = 'manual' as const

export default function NuevaCotizacion() {
  /*
    El mismo editor para los dos, con dos diferencias y las dos son de
    propiedad de los datos, no de permisos:

    - El tab «Análisis Financiero» son centrales de costo y costos estimados
      de 1Conecta. No son suyos, así que no los ve. El cronograma de pagos
      vivía ahí dentro y sí es suyo: se muda al tab de contenido.
    - «Equipo dedicado» se arma con `perfiles_equipo` —nuestros costos y
      tarifas por rol—, así que para él todo es `estandar`. El backend lo
      fuerza igual, esto es para no ofrecerle lo que va a rechazar.
  */
  const { isPartner } = useAuth()
  const db = useSupabase()
  const api = useApi()
  const nav = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  // Reference data
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [servicios, setServicios] = useState<Servicio[]>([])
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [centrales, setCentrales] = useState<CentralCosto[]>([])
  const [marcas, setMarcas] = useState<Marca[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Client contacts
  const [clienteContactos, setClienteContactos] = useState<(ContactoEmpresa & { contacto: Contacto })[]>([])

  // Form state
  const [numero, setNumero] = useState('')
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [contacto, setContacto] = useState('')
  const [telefono, setTelefono] = useState('')
  const [ruc, setRuc] = useState('')
  const [email, setEmail] = useState('')
  const [proyecto, setProyecto] = useState('')
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0])
  const [validez, setValidez] = useState(90)
  const [moneda, setMoneda] = useState<Moneda>('USD')
  const [estado, setEstado] = useState<EstadoCotizacion>('borrador')
  const [igvPct, setIgvPct] = useState(18)
  const [descPct, setDescPct] = useState(0)
  const [notas, setNotas] = useState('')
  const [items, setItems] = useState<CotizacionItemsEstandar[]>([])
  const [secciones, setSecciones] = useState<Seccion[]>([])
  // `cliente_nombre` tal como está guardado. El encabezado lo deriva de
  // `cliente_id`, pero una cotización sin cliente asignado (una propuesta que
  // todavía no tiene empresa en el directorio) no tiene de dónde derivarlo: sin
  // esto, guardar la dejaba con el encabezado vacío — pasó con las 29 y 30.
  const [clienteNombreGuardado, setClienteNombreGuardado] = useState('')

  // ─── Marca del documento y modo de precio ───
  // Las dos decisiones que más cambian la cara de una cotización. Vivían en
  // `cotizaciones.branding` y solo se podían escribir con un UPDATE a mano.
  //
  // `MARCA_MANUAL` cubre el branding puesto por SQL cuya marca no está en el
  // catálogo: guardar no lo pisa, lo conserva entero.
  const [marcaId, setMarcaId] = useState<number | typeof MARCA_MANUAL | null>(null)
  const [marca2Id, setMarca2Id] = useState<number | null>(null)
  const [brandingManual, setBrandingManual] = useState<CotizacionBranding | null>(null)
  const [modoPrecio, setModoPrecio] = useState<ModoPrecio>('total')

  // Sprint 3 — New fields
  const [contactoId, setContactoId] = useState<number | null>(null)
  const [vendedorNombre, setVendedorNombre] = useState('')
  const [gdocUrl, setGdocUrl] = useState('')
  const [costosEstimados, setCostosEstimados] = useState<CostoEstimadoLocal[]>([])
  const [cronogramaPagos, setCronogramaPagos] = useState<CuotaPago[]>([])

  // El markup del partner sobre nuestro costo. Se guarda en la cotización y
  // no se consume al aplicarlo, así que volver a esta pantalla lo muestra
  // como estaba y se puede ajustar partiendo del costo. Ver `lib/markup.ts`.
  const [markup, setMarkup] = useState<Markup | null>(null)

  // Editor tabs
  const [editorTab, setEditorTab] = useState<'general' | 'contenido' | 'analisis'>('general')

  // Google Doc & AI validation state
  const [creatingDoc, setCreatingDoc] = useState(false)
  const [validatingAI, setValidatingAI] = useState(false)
  const [aiValidation, setAiValidation] = useState<{ score: number; issues: string[]; suggestions: string[]; validado: boolean } | null>(null)

  // Mermaid preview
  const [mermaidPreview, setMermaidPreview] = useState<{ code: string; svg: string } | null>(null)
  const [mermaidLoading, setMermaidLoading] = useState(false)

  /*
    ¿El partner está re-preciando una cotización que armamos NOSOTROS?

    Se reconoce porque hay ítems con `costo_partner`. En ese caso el editor
    cambia de trabajo: él no redacta la propuesta ni arma el alcance —eso ya
    está hecho—, sólo le pone su precio encima. Así que la pantalla esconde
    todo lo que implicaría reescribir el documento (agregar servicios, ítems
    manuales, borrar líneas, tocar la descripción o la cantidad) y deja a la
    vista una sola cosa editable: cuánto le cobra a su cliente.
  */
  const soloReprecia = isPartner && items.some(it => Number(it?.costo_partner) > 0)

  // Calculations (derived — no state needed per skill guidance)
  const subtotal = r2(items.reduce((s, i) => s + r2(i.precio_unitario * i.cantidad), 0))
  const igvMonto = r2(subtotal * igvPct / 100)
  const descMonto = r2(subtotal * descPct / 100)
  const total = r2(subtotal + igvMonto - descMonto)

  // Load reference data + editing data
  useEffect(() => {
    (async () => {
      try {
        /*
          Cada pedido cae por separado y NINGUNO puede tumbar al editor.

          Iban todos crudos en el mismo `Promise.all`, así que el primero que
          fallara vaciaba a los demás: con un partner, `centrales_costo` es
          una ruta de admin y devolvía 403, y el editor abría sin clientes,
          sin servicios, sin empresa y sin marcas — todo vacío por una sola
          llamada que ni siquiera se usa en su pantalla. `/api/marcas` ya
          tenía su `.catch` por exactamente lo mismo; faltaba en el resto.

          Las centrales de costo directamente no se piden en modo partner: son
          del tab financiero, que él no ve.
        */
        const [c, s, e, allCot, cc, ms] = await Promise.all([
          db.getAll<Cliente>('clientes').catch(() => [] as Cliente[]),
          db.getAll<Servicio>('servicios').catch(() => [] as Servicio[]),
          db.getAll<Empresa>('empresa').catch(() => [] as Empresa[]),
          db.getAll<Cotizacion>('cotizaciones').catch(() => [] as Cotizacion[]),
          isPartner
            ? Promise.resolve([] as CentralCosto[])
            : db.getAll<CentralCosto>('centrales_costo').catch(() => [] as CentralCosto[]),
          api.get<Marca[]>('/api/marcas').catch(() => [] as Marca[]),
        ])
        setClientes(c); setServicios(s); setEmpresa(e[0] || null)
        setCentrales(cc.filter(x => x.activa).sort((a, b) => a.orden - b.orden))
        setMarcas(ms)

        if (isEdit) {
          const cot = await db.getById<Cotizacion>('cotizaciones', parseInt(id!))
          if (cot) {
            setNumero(cot.numero); setClienteId(cot.cliente_id); setContacto(cot.cliente_contacto ?? '')
            setClienteNombreGuardado(cot.cliente_nombre ?? '')
            setTelefono(cot.cliente_telefono ?? ''); setRuc(cot.cliente_ruc ?? ''); setEmail(cot.cliente_email ?? '')
            setProyecto(cot.proyecto_nombre ?? ''); setFecha(cot.fecha_emision ?? ''); setValidez(cot.validez_dias)
            setMoneda(cot.moneda as Moneda); setEstado(cot.estado); setIgvPct(cot.igv_porcentaje); setDescPct(cot.descuento_porcentaje)
            setNotas(cot.notas ?? ''); setItems(Array.isArray(cot.items) ? cot.items as CotizacionItemsEstandar[] : [])
            setSecciones(Array.isArray(cot.secciones) ? cot.secciones : [])

            // Load Sprint 3 extended data from cotizacion record
            const record = cot as unknown as Record<string, unknown>
            if (record.contacto_id) setContactoId(record.contacto_id as number)
            if (record.vendedor_nombre) setVendedorNombre(record.vendedor_nombre as string)
            if (record.gdoc_url) setGdocUrl(record.gdoc_url as string)
            if (Array.isArray(record.cronograma_pagos)) setCronogramaPagos(record.cronograma_pagos as CuotaPago[])
            // El markup del partner. Llega sólo en su propia sesión: el
            // sanitizador lo borra para todos los demás.
            if (record.markup) setMarkup(record.markup as Markup)

            // Marca y modo: se derivan de lo guardado. Una marca que no está en
            // el catálogo (branding escrito por SQL) entra como MARCA_MANUAL y
            // su objeto se conserva entero al guardar.
            const brand = (cot.branding ?? null) as CotizacionBranding | null
            setModoPrecio(modoDesdeCotizacion(cot))
            if (brand?.nombre) {
              const m = marcaPorNombre(ms, brand.nombre)
              if (m) setMarcaId(m.id)
              else { setMarcaId(MARCA_MANUAL); setBrandingManual(brand) }
              const m2 = marcaPorNombre(ms, brand.nombre_2)
              if (m2) setMarca2Id(m2.id)
            }

            // Load costos estimados from DB
            if (cot.cliente_id) loadContactos(cot.cliente_id)
            loadCostosEstimados(cot.id)
          }
        } else {
          setNumero(nextCotNumber(allCot.map(q => q.numero)))
        }
      } catch { showToast('Error al cargar datos', 'error') }
      finally { setLoading(false) }
    })()
  }, [id, isPartner])

  // Cambiar de modo toca el IGV, porque "Sin IGV" ES `igv_porcentaje = 0`: el
  // modo no agrega una columna nueva, escribe las que ya mandan el documento.
  const cambiarModo = (modo: ModoPrecio) => {
    setModoPrecio(modo)
    if (modo === 'sin_igv') setIgvPct(0)
    else if (modoLlevaIgv(modo) && igvPct === 0) setIgvPct(18)
  }

  const loadContactos = async (cId: number) => {
    try {
      const allCE = await db.getAll<ContactoEmpresa>('contacto_empresa')
      const allC = await db.getAll<Contacto>('contactos')
      const linked = allCE
        .filter(ce => ce.cliente_id === cId)
        .map(ce => ({ ...ce, contacto: allC.find(c => c.id === ce.contacto_id)! }))
        .filter(ce => ce.contacto)
      setClienteContactos(linked)
    } catch { /* non-critical */ }
  }

  const loadCostosEstimados = async (cotId: number) => {
    try {
      const all = await db.getAll<{ id: number; cotizacion_id: number; descripcion: string; monto: number; moneda: string; categoria: string }>('costos_estimados')
      const matching = all.filter(c => c.cotizacion_id === cotId)
      setCostosEstimados(matching.map(c => ({
        descripcion: c.descripcion,
        monto: c.monto,
        categoria: c.categoria as CostoEstimadoLocal['categoria'],
      })))
    } catch { /* non-critical */ }
  }

  // Fill client data on select
  const fillCliente = useCallback((cId: number) => {
    setClienteId(cId)
    const c = clientes.find(x => x.id === cId)
    if (c) setRuc(c.ruc_dni ?? '')
    loadContactos(cId)
    // Prefill con el contacto de FACTURACIÓN (fallback principal); editable en el selector.
    const fact = contactoFacturacionDe(c)
    if (fact) {
      setContacto([fact.nombre, fact.apellido].filter(Boolean).join(' '))
      setEmail(fact.email ?? '')
      setTelefono(fact.telefono ?? '')
      setContactoId(fact.id)
    } else {
      setContacto(''); setEmail(''); setTelefono(''); setContactoId(null)
    }
  }, [clientes])

  // Fill from contact select
  const fillContacto = (ceId: number) => {
    setContactoId(ceId)
    const linked = clienteContactos.find(x => x.contacto_id === ceId)
    if (linked?.contacto) {
      setContacto(`${linked.contacto.nombre}${linked.contacto.apellido ? ' ' + linked.contacto.apellido : ''}`)
      if (linked.contacto.email) setEmail(linked.contacto.email)
      if (linked.contacto.telefono) setTelefono(linked.contacto.telefono)
    }
  }

  // Add service from catalog
  const addServicio = useCallback((sId: number) => {
    const s = servicios.find(x => x.id === sId)
    if (!s) return
    /*
      Si el servicio es NUESTRO y lo agrega un partner, el precio del catálogo
      es lo que le cobramos a él: va como `costo_partner` y queda con el ítem,
      pase lo que pase con lo que él termine cobrando. `precio_unitario`
      arranca en ese mismo número sólo como punto de partida — es el campo que
      él edita, y el único que ve su cliente.
    */
    const esNuestro = isPartner && s.es_propio === false
    setItems(prev => [...prev, {
      descripcion: s.nombre,
      precio_unitario: s.precio_base,
      cantidad: 1,
      total: s.precio_base,
      // La moneda del costo sale del catálogo y no de la cotización: lo que
      // nos paga puede estar en otra moneda que lo que él cobra.
      ...(esNuestro
        ? { costo_partner: s.precio_base, costo_partner_moneda: s.moneda, servicio_id: s.id }
        : {}),
    }])
    if (secciones.length === 0 && Array.isArray(s.secciones) && s.secciones.length > 0) {
      setSecciones(s.secciones); showToast(`${s.secciones.length} secciones copiadas`, 'info')
    }
    if (!proyecto) setProyecto(s.nombre)
  }, [servicios, secciones.length, proyecto, isPartner])

  // Item mutations
  const updateItem = (idx: number, field: keyof CotizacionItemsEstandar, value: string) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it
      const updated = { ...it }
      if (field === 'descripcion') updated.descripcion = value
      else { (updated as unknown as Record<string, number>)[field] = parseFloat(value) || 0 }
      updated.total = r2(updated.precio_unitario * updated.cantidad)
      /*
        Tocar el precio de un ítem a mano con un markup puesto lo saca del
        markup: a partir de acá ese ítem conserva lo que el partner escribió.
        Sin esta marca, el siguiente ajuste global le pisaría el precio
        negociado sin decir nada. Se vuelve atrás poniéndolo en 0 y aplicando
        el markup otra vez.
      */
      if (field === 'precio_unitario' && markup && esMarcable(it, moneda)) {
        updated.precio_fijado = updated.precio_unitario > 0
      }
      return updated
    }))
  }
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx))
  const addEmpty = () => setItems(prev => [...prev, { descripcion: '', precio_unitario: 0, cantidad: 1, total: 0 }])

  // Save
  const save = async () => {
    if (items.length === 0) { showToast('Agrega al menos un item', 'error'); return }
    setSaving(true)
    try {
      // Con cliente asignado el nombre sale del directorio (fuente única); sin
      // cliente se conserva el que ya tenía, en vez de vaciar el encabezado.
      const clienteNombre = clienteId
        ? (clientes.find(c => c.id === clienteId)?.empresa ?? clienteNombreGuardado)
        : clienteNombreGuardado
      const data: Record<string, unknown> = {
        numero, empresa_id: empresa?.id ?? 1, cliente_id: clienteId, cliente_nombre: clienteNombre,
        cliente_contacto: contacto, cliente_telefono: telefono, cliente_ruc: ruc, cliente_email: email,
        fecha_emision: fecha, validez_dias: validez, proyecto_nombre: proyecto,
        items, subtotal, igv_monto: igvMonto,
        descuento_porcentaje: descPct, descuento_monto: descMonto, total, moneda, estado,
        metodos_pago: ['Transferencia bancaria - clientes Peru', 'Tarjeta de debito/credito - internacional', 'Criptomonedas - internacional'],
        secciones, notas, tipo_cotizacion: 'estandar',
        // Sprint 3 fields
        contacto_id: contactoId,
        vendedor_nombre: vendedorNombre || null,
        gdoc_url: gdocUrl || null,
        // Modo de precio: `mostrar_precios` + `igv_porcentaje` (y, abajo,
        // `branding.presupuesto_mas_igv`). Tres campos que se usan como un
        // conjunto excluyente, escritos desde un solo selector.
        ...camposDesdeModo(modoPrecio, igvPct),
        // Identidad del documento. Sin marca queda `null` salvo que el modo
        // "+ IGV" necesite guardarse — y entonces va sin `nombre`, que es el
        // campo del que las tres superficies cuelgan el white-label.
        branding: armarBranding({
          modo: modoPrecio,
          marca: marcaId !== null && marcaId !== MARCA_MANUAL ? marcas.find(m => m.id === marcaId) ?? null : null,
          marca2: marca2Id !== null ? marcas.find(m => m.id === marca2Id) ?? null : null,
          brandingManual: marcaId === MARCA_MANUAL ? brandingManual : null,
        }),
        cronograma_pagos: cronogramaPagos.length > 0 ? cronogramaPagos : null,
        // El markup del partner. Va siempre —también en `null`— para que
        // "volver a los precios base" se guarde: omitirlo dejaría el markup
        // viejo en la fila con los ítems ya sin marcar.
        markup,
      }

      let cotId: number
      if (isEdit) {
        data.id = parseInt(id!)
        await db.update('cotizaciones', data)
        cotId = parseInt(id!)
        showToast('Cotizacion actualizada', 'success')
      } else {
        cotId = await db.add('cotizaciones', data)
        showToast('Cotizacion creada', 'success')
      }

      // Los costos estimados son nuestros: el partner no tiene ninguno y la
      // ruta es de admin, así que pedirla devolvería 403 después de haber
      // guardado bien la cotización.
      if (!isPartner) await saveCostosEstimados(cotId)

      nav(isPartner ? '/partner/cartera/cotizaciones' : '/cotizaciones')
    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al guardar', 'error') }
    finally { setSaving(false) }
  }

  const saveCostosEstimados = async (cotId: number) => {
    try {
      // Delete existing and re-insert
      const existing = await db.getAll<{ id: number; cotizacion_id: number }>('costos_estimados')
      const toDelete = existing.filter(c => c.cotizacion_id === cotId)
      for (const c of toDelete) { await db.remove('costos_estimados', c.id) }

      // Insert new
      for (const c of costosEstimados) {
        if (c.descripcion.trim() || c.monto > 0) {
          await db.add('costos_estimados', {
            cotizacion_id: cotId,
            descripcion: c.descripcion,
            monto: r2(c.monto),
            moneda,
            categoria: c.categoria,
          })
        }
      }
    } catch { /* best effort */ }
  }

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><PageHeader breadcrumbs={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: isEdit ? 'Editar' : 'Nueva' }]} /><div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      {/* ─── UNIFIED STICKY HEADER ─── */}
      <div className="nueva-cot-sticky-header">
        <div className="nueva-cot-header-top">
          <div className="nueva-cot-header-info">
            <Breadcrumbs
              items={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: isEdit ? 'Editar' : 'Nueva' }]}
              meta={`N. ${numero}`}
            />
          </div>
          <div className="nueva-cot-header-actions">
            <button className="btn btn-secondary" onClick={() => nav(isEdit ? `/cotizaciones/${id}` : '/cotizaciones')}>← Volver</button>
            <button className="btn btn-success" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar Cambios'}</button>
          </div>
        </div>
        <div className="nueva-cot-tabs">
          {([
            { key: 'general' as const, label: 'Información General', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> },
            { key: 'contenido' as const, label: 'Contenido de la Propuesta', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> },
            { key: 'analisis' as const, label: 'Análisis Financiero', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg> },
          ]).filter(tab => {
            // «Análisis Financiero» son NUESTROS costos: nunca para un partner.
            if (isPartner && tab.key === 'analisis') return false;
            // Y el contenido de la propuesta tampoco cuando sólo re-precia:
            // el documento ya está escrito y lo que falta es el precio.
            if (soloReprecia && tab.key === 'contenido') return false;
            return true;
          }).map(tab => (
            <button
              key={tab.key}
              className={`nueva-cot-tab ${editorTab === tab.key ? 'nueva-cot-tab--active' : ''}`}
              onClick={() => setEditorTab(tab.key)}
              type="button"
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── MAIN LAYOUT ─── */}
      <div className="nueva-cot-layout">
        {/* MAIN CONTENT AREA */}
        <div className="nueva-cot-main">
          {/* TAB 1: Información General */}
          {editorTab === 'general' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card">
                <div className="card-header"><h3>Datos del Cliente</h3></div>
                <div className="form-group">
                  <label className="form-label">Cliente</label>
                  <select
                    className="form-select"
                    value={clienteId ?? (clienteNombreGuardado ? SIN_EMPRESA : '')}
                    disabled={soloReprecia}
                    onChange={e => fillCliente(parseInt(e.target.value))}
                  >
                    {/*
                      Una cotización puede estar hecha a una oportunidad cuya
                      empresa todavía no existe en el directorio —lo normal
                      cuando se cotiza antes de cerrar—. Sin esta opción el
                      selector decía «-- Seleccionar cliente --» y parecía que
                      el dato se había perdido, cuando el nombre está guardado
                      en la cotización y es el que sale impreso.
                    */}
                    {!clienteId && clienteNombreGuardado
                      ? <option value={SIN_EMPRESA}>{clienteNombreGuardado} — aún no está en tu cartera</option>
                      : <option value="">-- Seleccionar cliente --</option>}
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                  </select>
                </div>
                {clienteContactos.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Contacto (persona)</label>
                    <select className="form-select" value={contactoId ?? ''} onChange={e => fillContacto(parseInt(e.target.value))}>
                      <option value="">-- Seleccionar contacto --</option>
                      {clienteContactos.map(ce => (
                        <option key={ce.contacto_id} value={ce.contacto_id}>{ce.contacto.nombre} {ce.contacto.apellido ?? ''} {ce.cargo_empresa ? `(${ce.cargo_empresa})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Contacto</label><input className="form-input" value={contacto} onChange={e => setContacto(e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">RUC/DNI</label><input className="form-input" value={ruc} onChange={e => setRuc(e.target.value)} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={telefono} onChange={e => setTelefono(e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={email} onChange={e => setEmail(e.target.value)} /></div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><h3>Proyecto</h3></div>
                <div className="form-group"><label className="form-label">Nombre</label><input className="form-input" value={proyecto} onChange={e => setProyecto(e.target.value)} placeholder="Ej: Diagnóstico Comercial" /></div>
                <div className="form-group"><label className="form-label">Vendedor</label><input className="form-input" value={vendedorNombre} onChange={e => setVendedorNombre(e.target.value)} placeholder="Nombre del vendedor" /></div>
              </div>

              {/* Marca del documento y modo de precio. Las dos decisiones que más
                  cambian la cara de la cotización, y las dos que hasta oct-2026
                  solo se podían escribir con un UPDATE contra producción. */}
              <div className="card">
                <div className="card-header"><h3>Marca y precio</h3></div>
                <div className="form-group">
                  <label className="form-label">Marca del documento</label>
                  <select
                    className="form-select"
                    value={marcaId === null ? '' : String(marcaId)}
                    onChange={e => {
                      const v = e.target.value
                      if (!v) { setMarcaId(null); setMarca2Id(null) }
                      else if (v === MARCA_MANUAL) setMarcaId(MARCA_MANUAL)
                      else setMarcaId(parseInt(v))
                    }}
                  >
                    <option value="">1Conecta (cotización propia)</option>
                    {marcas.filter(m => m.activa || m.id === marcaId).map(m => (
                      <option key={m.id} value={m.id}>{m.nombre}{m.activa ? '' : ' (inactiva)'}</option>
                    ))}
                    {marcaId === MARCA_MANUAL && (
                      <option value={MARCA_MANUAL}>
                        {brandingManual?.nombre ?? 'Marca'} — definida a mano
                      </option>
                    )}
                  </select>
                  <div className="form-hint">
                    {marcaId === null
                      ? 'El documento sale con la identidad de 1Conecta: razón social, RUC y datos bancarios.'
                      : marcaId === MARCA_MANUAL
                        ? 'Esta marca no está en el catálogo (se puso a mano). Se conserva tal cual al guardar.'
                        : 'Marca blanca: el documento oculta razón social, RUC, datos bancarios y el pie de 1Conecta.'}
                  </div>
                </div>

                {marcaId !== null && (
                  <div className="form-group">
                    <label className="form-label">Segunda marca (co-branding)</label>
                    <select className="form-select" value={marca2Id ?? ''}
                      onChange={e => setMarca2Id(e.target.value ? parseInt(e.target.value) : null)}>
                      <option value="">Sin segunda marca</option>
                      {marcas.filter(m => m.id !== marcaId && (m.activa || m.id === marca2Id)).map(m => (
                        <option key={m.id} value={m.id}>{m.nombre}{m.activa ? '' : ' (inactiva)'}</option>
                      ))}
                    </select>
                    <div className="form-hint">Va arriba a la derecha, donde iría el RUC del emisor.</div>
                  </div>
                )}

                {marcaId !== null && (() => {
                  const mSel = marcaId === MARCA_MANUAL ? null : marcas.find(m => m.id === marcaId)
                  const logo = marcaId === MARCA_MANUAL ? brandingManual?.logo_url : mSel?.logo_data_uri
                  const m2 = marca2Id !== null ? marcas.find(m => m.id === marca2Id) : null
                  return (
                    <div className="marca-logo-preview">
                      <span className="marca-logo-preview__label">Cabecera del documento</span>
                      {logo
                        ? <span className="marca-logo-chip"><img src={logo} alt="" /></span>
                        : <span style={{ fontSize: '0.78rem', color: 'var(--warning)' }}>
                            Esta marca no tiene logo: el documento imprimirá su nombre.
                          </span>}
                      {m2?.logo_data_uri && <span className="marca-logo-chip"><img src={m2.logo_data_uri} alt="" /></span>}
                    </div>
                  )
                })()}

                <div className="form-group">
                  <label className="form-label">Modo de precio</label>
                  <select className="form-select" value={modoPrecio} onChange={e => cambiarModo(e.target.value as ModoPrecio)}>
                    {MODOS_PRECIO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <div className="form-hint">{MODOS_PRECIO.find(m => m.value === modoPrecio)?.hint}</div>
                </div>

                {modoLlevaIgv(modoPrecio) && (
                  <div className="form-group">
                    <label className="form-label">IGV %</label>
                    <input className="form-input" type="number" value={igvPct}
                      onChange={e => setIgvPct(parseFloat(e.target.value) || 0)} />
                  </div>
                )}
              </div>

              <div className="card">
                <div className="card-header"><h3>Condiciones</h3></div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Fecha</label><input className="form-input" type="date" value={fecha} onChange={e => setFecha(e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Validez (días)</label><input className="form-input" type="number" value={validez} onChange={e => setValidez(parseInt(e.target.value) || 90)} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Moneda</label><select className="form-select" value={moneda} onChange={e => setMoneda(e.target.value as Moneda)}><option value="USD">USD</option><option value="PEN">PEN</option><option value="EUR">EUR</option></select></div>
                  <div className="form-group"><label className="form-label">Estado</label><select className="form-select" value={estado} onChange={e => setEstado(e.target.value as EstadoCotizacion)}><option value="borrador">Borrador</option><option value="enviada">Enviada</option><option value="aceptada">Aceptada</option><option value="rechazada">Rechazada</option></select></div>
                </div>
                <div className="form-group"><label className="form-label">Descuento %</label><input className="form-input" type="number" value={descPct} onChange={e => setDescPct(parseFloat(e.target.value) || 0)} /></div>
              </div>

              {/* Crear o validar un Google Doc es trabajar sobre el documento,
                  y el documento ya está hecho: acá sólo se le pone precio. */}
              {!soloReprecia && (
              <div className="card">
                <div className="card-header"><h3>Documento Google</h3></div>
                <div className="cotizacion-gdoc-field">
                  <div className="form-group">
                    <label className="form-label">URL Google Doc</label>
                    <input className="form-input" value={gdocUrl} onChange={e => setGdocUrl(e.target.value)} placeholder="https://docs.google.com/..." />
                  </div>
                  {gdocUrl && (
                    <a href={gdocUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ height: 40 }}>
                      <IconExternalLink size={14} /> Abrir
                    </a>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" type="button" disabled={creatingDoc || !isEdit} onClick={async () => {
                    if (!isEdit) { showToast('Guarda la cotización primero', 'info'); return }
                    setCreatingDoc(true)
                    try {
                      const { data, error } = await supabase.functions.invoke('google-create-doc', { body: { cotizacion_id: parseInt(id!) } })
                      if (error) throw error
                      if (data?.ok && data.doc_url) { setGdocUrl(data.doc_url); window.open(data.doc_url, '_blank'); showToast('Google Doc creado', 'success') }
                      else { showToast(data?.message ?? 'Error al crear documento', 'error') }
                    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al crear Google Doc', 'error') }
                    finally { setCreatingDoc(false) }
                  }}>
                    {creatingDoc ? <><div className="auth-spinner" style={{ width: 14, height: 14 }} /> Creando...</> : <><IconFileText size={14} /> Crear Google Doc</>}
                  </button>
                  <button className="btn btn-secondary btn-sm" type="button" disabled={validatingAI || !gdocUrl || !isEdit} onClick={async () => {
                    if (!isEdit) { showToast('Guarda la cotización primero', 'info'); return }
                    if (!gdocUrl) { showToast('Crea o agrega un Google Doc primero', 'info'); return }
                    setValidatingAI(true); setAiValidation(null)
                    try {
                      const { data, error } = await supabase.functions.invoke('gemini-validate-doc', { body: { cotizacion_id: parseInt(id!) } })
                      if (error) throw error
                      if (data?.ok) { setAiValidation({ score: data.score, issues: data.issues ?? [], suggestions: data.suggestions ?? [], validado: data.validado }); showToast(`Validación completada: ${data.score}/10`, data.validado ? 'success' : 'info') }
                      else { showToast(data?.message ?? 'Error al validar', 'error') }
                    } catch (err) { showToast(err instanceof Error ? err.message : 'Error al validar con IA', 'error') }
                    finally { setValidatingAI(false) }
                  }}>
                    {validatingAI ? <><div className="auth-spinner" style={{ width: 14, height: 14 }} /> Validando...</> : <><IconCheckCircle size={14} /> Validar con IA</>}
                  </button>
                </div>
                {aiValidation && (
                  <div className="ai-validation-result">
                    <div className="ai-validation-result__header">
                      <span className={`ai-score-badge ${aiValidation.score >= 7 ? 'ai-score-badge--success' : aiValidation.score >= 5 ? 'ai-score-badge--warning' : 'ai-score-badge--danger'}`}>{aiValidation.score}/10</span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{aiValidation.validado ? 'Aprobada' : 'Requiere mejoras'}</span>
                    </div>
                    {aiValidation.issues.length > 0 && (
                      <div className="ai-validation-result__section">
                        <h4 className="ai-validation-result__title"><IconAlertCircle size={14} style={{ color: 'var(--danger)' }} /> Problemas</h4>
                        <ul className="ai-validation-result__list">{aiValidation.issues.map((issue, i) => <li key={i}>{issue}</li>)}</ul>
                      </div>
                    )}
                    {aiValidation.suggestions.length > 0 && (
                      <div className="ai-validation-result__section">
                        <h4 className="ai-validation-result__title"><IconCheckCircle size={14} style={{ color: 'var(--info)' }} /> Sugerencias</h4>
                        <ul className="ai-validation-result__list">{aiValidation.suggestions.map((sug, i) => <li key={i}>{sug}</li>)}</ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* TAB 2: Contenido de la Propuesta */}
          {editorTab === 'contenido' && (
            <div className="card">
              <div className="card-header"><h3>Secciones de Propuesta</h3><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{secciones.length} sección(es)</span></div>
              {secciones.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '8px 0' }}>Sin secciones. Agrega un servicio para cargar su plantilla o crea secciones manualmente.</p>
              ) : (
                secciones.map((sec, i) => (
                  <details key={i} style={{ borderBottom: '1px solid var(--border)' }} open={i === 0}>
                    <summary style={{ padding: '10px 12px', cursor: 'pointer', fontWeight: 600, background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1 }}>{i + 1}. {sec.titulo || '(sin título)'}</span>
                      <button className="btn btn-sm" style={{ color: 'var(--danger)', background: 'none', border: '1px solid var(--border)', padding: '2px 8px', fontSize: '0.75rem' }}
                        onClick={e => { e.preventDefault(); setSecciones(prev => prev.filter((_, j) => j !== i)) }}>x</button>
                    </summary>
                    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div>
                        <label className="form-label">Título</label>
                        <input className="form-input" value={sec.titulo} onChange={e => setSecciones(prev => prev.map((s, j) => j === i ? { ...s, titulo: e.target.value } : s))} />
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <label className="form-label" style={{ margin: 0 }}>Contenido (HTML / Mermaid)</label>
                          <button className="btn btn-sm" style={{ fontSize: '0.72rem', padding: '2px 10px', background: 'var(--primary)', color: 'var(--text-on-primary)', border: 'none', borderRadius: 4 }}
                            onClick={async () => {
                              setMermaidLoading(true)
                              try {
                                const blocks = parseMermaidBlocks(sec.contenido)
                                if (blocks.length === 0) { showToast('No se encontraron bloques Mermaid', 'info'); return }
                                const m = (await import('mermaid')).default
                                let svgAcc = ''
                                for (const block of blocks) {
                                  const code = block.code.replace(/\\n/g, '\n')
                                  const id = `mermaid-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`
                                  const { svg } = await m.render(id, code)
                                  svgAcc += svg
                                }
                                setMermaidPreview({ code: sec.contenido, svg: svgAcc })
                              } catch (e) { showToast('Error al renderizar Mermaid: ' + (e instanceof Error ? e.message : 'desconocido'), 'error') }
                              finally { setMermaidLoading(false) }
                            }}
                          >{mermaidLoading ? 'Renderizando...' : '🔍 Vista Previa Mermaid'}</button>
                        </div>
                        <textarea className="form-textarea" rows={14} value={sec.contenido}
                          onChange={e => setSecciones(prev => prev.map((s, j) => j === i ? { ...s, contenido: e.target.value } : s))}
                          style={{ fontFamily: '"Fira Code","Cascadia Code","Consolas",monospace', fontSize: '0.78rem', lineHeight: 1.6 }} spellCheck={false} />
                        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
                          Usa etiquetas HTML para formato. Bloques Mermaid con <code>```mermaid\n...\n```</code>.
                        </p>
                      </div>
                    </div>
                  </details>
                ))
              )}
              <button className="btn btn-secondary btn-sm" style={{ margin: '12px' }}
                onClick={() => setSecciones(prev => [...prev, { titulo: 'Nueva Sección', contenido: '' }])}>
                + Agregar sección
              </button>
            </div>
          )}

          {/* TAB 3: Análisis Financiero — nuestro, no del partner */}
          {editorTab === 'analisis' && !isPartner && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <CostCenterBreakdown subtotal={subtotal} moneda={moneda} costosEstimados={costosEstimados} centrales={centrales} />
              <CostosEstimados costos={costosEstimados} onUpdate={setCostosEstimados} moneda={moneda} />
              <CronogramaPagos cuotas={cronogramaPagos} total={subtotal} moneda={moneda} onUpdate={setCronogramaPagos} />
            </div>
          )}

          {/* Los hitos de cobro son del documento que firma su cliente, así que
              son suyos: al esconder el tab financiero se mudan acá. */}
          {editorTab === 'contenido' && isPartner && (
            <div style={{ marginTop: 16 }}>
              <CronogramaPagos cuotas={cronogramaPagos} total={subtotal} moneda={moneda} onUpdate={setCronogramaPagos} />
            </div>
          )}
        </div>
      </div>

      {/* ─── MARKUP DEL PARTNER ─── */}
      {/* Antes de los ítems a propósito: es la decisión que fija sus precios,
          y después de verla el partner casi nunca necesita tocarlos uno a uno. */}
      {isPartner && (
        <div className="nueva-cot-items-section">
          <MarkupPartner
            items={items}
            moneda={moneda}
            markup={markup}
            onAplicar={(m, nuevos) => {
              const antes = subtotal
              setMarkup(m)
              setItems(nuevos)
              // El cronograma lleva montos propios y el pie del documento suma
              // LO IMPRESO: sin esto, los hitos seguirían en el precio viejo y
              // la página de Inversión se contradiría con la del cronograma.
              setCronogramaPagos(prev => {
                const despues = r2(nuevos.reduce((s, i) => s + r2(i.precio_unitario * i.cantidad), 0))
                return (escalarCronograma(prev, antes, despues) ?? []) as CuotaPago[]
              })
              showToast('Precios actualizados con tu markup', 'success')
            }}
            onQuitar={() => {
              const antes = subtotal
              // Volver al costo es aplicar factor 1: los ítems quedan en lo
              // que nos pagas, que es de donde partió todo.
              const base = aplicarMarkup(
                items,
                { tipo: 'porcentaje', valor: 0, factor: 1, aplicado_at: new Date().toISOString() },
                moneda,
              )
              setMarkup(null)
              setItems(base)
              setCronogramaPagos(prev => {
                const despues = r2(base.reduce((s, i) => s + r2(i.precio_unitario * i.cantidad), 0))
                return (escalarCronograma(prev, antes, despues) ?? []) as CuotaPago[]
              })
              showToast('La cotización volvió a los precios base', 'info')
            }}
          >
            {/*
              La tabla del partner. La diferencia con la de siempre no es
              cosmética: antes había UNA columna de precio, que era la nuestra
              y la suya a la vez, así que al escribir encima pisaba nuestro
              número creyendo que ponía el suyo — y perdía la referencia de
              cuánto le costaba.

              Ahora son dos columnas: lo que nos paga (fijo) y lo que cobra
              (editable). La cantidad y la descripción no se tocan: vienen de
              una propuesta ya acordada.
            */}
            <div className="repre-tabla">
              <div className="repre-fila repre-fila--cab">
                <span>Descripción</span>
                <span>Nos pagas</span>
                <span>Cant.</span>
                <span>Tu precio</span>
                <span>Total</span>
                <span>Te queda</span>
              </div>
              {items.map((it, i) => {
                const costoUnit = Number(it.costo_partner ?? 0)
                const costo = r2(costoUnit * it.cantidad)
                const total = r2(it.precio_unitario * it.cantidad)
                const queda = r2(total - costo)
                const mismaMoneda = (it.costo_partner_moneda || moneda) === moneda
                return (
                  <div className="repre-fila" key={i}>
                    <span className="repre-desc" title={it.descripcion}>{it.descripcion}</span>
                    <span className="repre-costo">
                      {costoUnit > 0 ? formatCurrency(costo, it.costo_partner_moneda || moneda) : '—'}
                    </span>
                    <span className="repre-cant">{it.cantidad}</span>
                    <input
                      className="form-input repre-precio"
                      type="number"
                      step="0.01"
                      min="0"
                      value={it.precio_unitario}
                      onChange={e => updateItem(i, 'precio_unitario', e.target.value)}
                      aria-label={`Tu precio para ${it.descripcion}`}
                    />
                    <span className="repre-total">{formatCurrency(total, moneda)}</span>
                    {/* En verde lo que gana. Con el costo en otra moneda no se
                        puede restar, así que se calla en vez de inventar. */}
                    <span className={`repre-queda ${queda > 0 ? 'repre-queda--ok' : ''}`}>
                      {costoUnit > 0 && mismaMoneda ? formatCurrency(queda, moneda) : '—'}
                    </span>
                  </div>
                )
              })}
            </div>
          </MarkupPartner>
        </div>
      )}

      {/* ─── ITEMS / SERVICIOS (full-width below form) ─── */}
      {/* Cuando el partner sólo re-precia, la tabla vive DENTRO del panel de
          markup: poner el número y ver qué le hace a cada línea es un solo
          gesto. */}
      {!soloReprecia && (
      <div className="nueva-cot-items-section">
        <div className="card">
          <div className="card-header">
            <h3>{soloReprecia ? 'Precios' : 'Items / Servicios'}</h3>
            {/* Agregar servicios es armar el alcance, y el alcance de una
                cotización nuestra ya está cerrado: acá sólo se le pone precio. */}
            {!soloReprecia && (
              <div style={{ display: 'flex', gap: 6 }}>
                <select className="form-select" id="add-srv" style={{ width: 'auto', padding: '6px 30px 6px 10px', fontSize: '0.82rem' }} defaultValue="">
                  <option value="">+ Agregar servicio</option>
                  {servicios.map(s => <option key={s.id} value={s.id}>{s.nombre} ({formatCurrency(s.precio_base, s.moneda)})</option>)}
                </select>
                <button className="btn btn-primary btn-sm" onClick={() => { const el = document.getElementById('add-srv') as HTMLSelectElement; const v = parseInt(el?.value); if (v) { addServicio(v); el.value = '' } }}>Agregar</button>
              </div>
            )}
          </div>
          <div className="items-editor">
            <div className="item-row" style={{ fontWeight: 600, fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              <span>Descripción</span><span>P. Unit.</span><span>Cant.</span><span>Total</span><span></span>
            </div>
            {items.map((it, i) => (
              <div key={i}>
                <div className="item-row">
                  <input className="form-input" value={it.descripcion} onChange={e => updateItem(i, 'descripcion', e.target.value)} />
                  <input className="form-input" type="number" step="0.01" value={it.precio_unitario} onChange={e => updateItem(i, 'precio_unitario', e.target.value)} />
                  <input className="form-input" type="number" min="1" value={it.cantidad} onChange={e => updateItem(i, 'cantidad', e.target.value)} />
                  <input className="form-input" value={r2(it.precio_unitario * it.cantidad).toFixed(2)} readOnly style={{ opacity: 0.7 }} />
                  <button className="remove-item" onClick={() => removeItem(i)}>x</button>
                </div>
                {/* Lo que este ítem le cuesta al partner, y cuánto le queda.
                    Sin el número a la vista, poner el precio es adivinar. Esta
                    línea no sale del editor: el sanitizador borra
                    `costo_partner` en las tres rutas públicas. */}
                {isPartner && it.costo_partner != null && (() => {
                  const monedaCosto = it.costo_partner_moneda || moneda
                  const costo = r2(it.costo_partner! * it.cantidad)
                  return (
                    <p className="item-row__costo">
                      Te cuesta {formatCurrency(costo, monedaCosto)}
                      {/* El margen solo si las dos monedas son la misma: restar
                          soles menos dólares da un número que no existe. */}
                      {monedaCosto === moneda && (
                        <>
                          {' · '}
                          <strong>
                            te quedan {formatCurrency(r2(it.precio_unitario * it.cantidad - costo), moneda)}
                          </strong>
                        </>
                      )}
                    </p>
                  )
                })()}
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={addEmpty}>+ Item manual</button>
          </div>
          <div className="nueva-cot-items-totals">
            <div><span>Subtotal</span><strong>{subtotal.toFixed(2)}</strong></div>
            <div><span>IGV ({igvPct}%)</span><strong>{igvMonto.toFixed(2)}</strong></div>
            <div><span>Descuento</span><strong>-{descMonto.toFixed(2)}</strong></div>
            <div className="nueva-cot-items-grand-total"><span>TOTAL</span><strong>{total.toFixed(2)} {moneda}</strong></div>
          </div>
        </div>
      </div>
      )}

      {/* Las notas, al final de todo: son el último repaso antes de guardar,
          no un dato del encabezado. Salen impresas en el documento. */}
      {editorTab === 'general' && (
        <div className="nueva-cot-items-section">
          <div className="card">
            <div className="card-header"><h3>Notas</h3></div>
            <textarea
              className="form-textarea"
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Notas adicionales..."
            />
          </div>
        </div>
      )}

      {/* ─── MERMAID PREVIEW MODAL ─── */}
      {mermaidPreview && (
        <div className="modal-backdrop" onClick={() => setMermaidPreview(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 820, maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>Vista Previa — Diagramas Mermaid</h3>
              <button className="modal-close" onClick={() => setMermaidPreview(null)}>x</button>
            </div>
            <div style={{ padding: 20, background: '#fff' }} dangerouslySetInnerHTML={{ __html: mermaidPreview.svg }} />
          </div>
        </div>
      )}
    </div>
  )
}
